import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  List,
  Divider,
  LinearProgress,
} from "@mui/material";
import { Close, Add, Save } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CustomTextField from "../../shared/textFields/CustomTextField";
import CustomButton from "../../shared/button/CustomButton";
import CustomLoadingButton from "../../shared/button/CustomLoadingButton";
import { useTemplateChecklistEditor } from "./useTemplateChecklistEditor.js";
import TemplateChecklistItemEditor from "./TemplateChecklistItemEditor";

export default function TemplateChecklistEditor({ open, onClose, pdcId, template, isCreating }) {
  const {
    templateTitle,
    items,
    newItemContent,
    isLoading,
    isSaving,
    handleAddItem,
    handleRemoveItem,
    handleItemContentChange,
    handleDragEnd,
    handleSave,
    canSave,
  } = useTemplateChecklistEditor({
    pdcId,
    template,
    isCreating,
    onSuccess: onClose,
  });

  const pdcName = template?.powerDistributionCompany?.name || "Nova Concessionária";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "600px" },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" component="div">
              {isCreating ? "Criar" : "Editar"} Template de Checklist
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pdcName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {isLoading && <LinearProgress />}

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ mb: 3 }}>
          <CustomTextField
            params={templateTitle}
            label="Título do Template"
            placeholder="Ex: Checklist Padrão CPFL"
            required
            fullWidth
            disabled={isSaving}
          />
        </Box>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Itens do Checklist
          <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
            ({items.length} {items.length === 1 ? "item" : "itens"})
          </Typography>
        </Typography>

        {/* Add New Item */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <CustomTextField
              params={newItemContent}
              label="Novo item"
              placeholder="Digite o conteúdo do novo item..."
              fullWidth
              disabled={isSaving}
            />
            <CustomButton
              buttonText="Adicionar"
              startIcon={<Add />}
              handleButtonClick={handleAddItem}
              disabled={!newItemContent.value.trim() || isSaving}
              sx={{ minWidth: "auto", px: 2 }}
            />
          </Box>
        </Box>

        {/* Items List */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="checklist-items">
            {(provided, snapshot) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{
                  bgcolor: snapshot.isDraggingOver ? "action.hover" : "transparent",
                  borderRadius: 1,
                  transition: "background-color 0.2s ease",
                  minHeight: items.length === 0 ? 100 : "auto",
                }}
              >
                {items.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 100,
                      color: "text.secondary",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="body2">Nenhum item adicionado ainda</Typography>
                    <Typography variant="caption">
                      Adicione itens acima para criar o template
                    </Typography>
                  </Box>
                ) : (
                  items.map((item, index) => (
                    <Draggable
                      key={item.id || `item-${index}`}
                      draggableId={item.id?.toString() || `item-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <TemplateChecklistItemEditor
                          item={item}
                          index={index}
                          onContentChange={handleItemContentChange}
                          onRemove={handleRemoveItem}
                          isSaving={isSaving}
                          provided={provided}
                          snapshot={snapshot}
                        />
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          {items.length} {items.length === 1 ? "item" : "itens"} • Arraste para reordenar
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomButton
            buttonText="Cancelar"
            handleButtonClick={onClose}
            disabled={isSaving}
            variant="outlined"
          />

          <CustomLoadingButton
            buttonText={isCreating ? "Criar Template" : "Salvar Alterações"}
            startIcon={<Save />}
            handleButtonClick={handleSave}
            disabled={!canSave}
            loading={isSaving}
            variant="contained"
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
